import React, { useRef, useState } from 'react';
import {
	Box,
	Button,
	Paper,
	Typography,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Divider,
	Grid,
	Chip,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, PendingActions as PendingIcon, Edit as EditIcon } from '@mui/icons-material';
// @ts-ignore
import WebViewer from '@pdftron/webviewer';
import { OnboardingPhaseTwoDocumentStatus, uploadOnboardingPhaseTwoDocument } from 'core/API/onboarding';
import { IOnboardingDocument } from 'core/interfaces/onboardingInterface';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import {
	getWebViewerContainerSx,
	getWebViewerInitOptions,
	prepareWebViewerInstance,
	setupWebViewerOnDocumentLoaded,
} from 'core/services/webViewerSetup';
import { attachWebViewerOverscrollToPage } from 'core/services/webViewerOverscroll';

interface OnboardingDocumentViewerProps {
	documentStatuses: OnboardingPhaseTwoDocumentStatus[];
	documentTemplates: IOnboardingDocument[];
	onboardingId: number;
	onRefresh: () => Promise<void>;
	onAllDocumentsComplete: () => void;
	readOnly?: boolean;
}

function getPdfUrl(path: string): string {
	return `${process.env.REACT_APP_IMAGE_URL}/onboarding_templates/${path}`;
}

function getFilledPdfUrl(path: string): string {
	return `${process.env.REACT_APP_IMAGE_URL}/onboarding_employee/${path}`;
}

export function OnboardingDocumentViewer({
	documentStatuses,
	documentTemplates,
	onboardingId,
	onRefresh,
	onAllDocumentsComplete,
	readOnly = false,
}: OnboardingDocumentViewerProps): JSX.Element {
	const theme = useTheme();
	const isTabletViewport = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
	const viewer = useRef<HTMLDivElement>(null);
	const instance = useRef<any>(null);
	const isInitializing = useRef<boolean>(false);
	// Default to first pending document, or 0 if all are completed
	const [currentDocIndex, setCurrentDocIndex] = useState(() => {
		const firstPending = documentStatuses.findIndex(
			(doc) => doc.status !== 'completed' && doc.status !== 'approved'
		);
		return firstPending !== -1 ? firstPending : 0;
	});
	const [isUploading, setIsUploading] = useState(false);
	const [isViewerReady, setIsViewerReady] = useState(false);
	const [editingDocIndices, setEditingDocIndices] = useState<Set<number>>(new Set());
	const { showToast } = useToastContext();

	// Keep a local copy of document statuses so we can update without full reload
	const [localDocStatuses, setLocalDocStatuses] = useState(documentStatuses || []);

	// Sync from parent if the prop changes (e.g. on initial load)
	// Only update if the content actually changed, not just reference
	React.useEffect(() => {
		if (documentStatuses?.length && JSON.stringify(documentStatuses) !== JSON.stringify(localDocStatuses)) {
			setLocalDocStatuses(documentStatuses);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentStatuses]);

	// Map documentStatuses to their corresponding templates
	const documents = localDocStatuses;
	const currentStatus = documents[currentDocIndex];
	const currentTemplate = currentStatus
		? documentTemplates.find((template) => template.id === currentStatus.template_id)
		: null;

	// For completed docs, always show the filled PDF (even when editing, so user sees their previous work);
	// for pending docs, show the blank template
	const getDocPdfUrl = (docStatus: OnboardingPhaseTwoDocumentStatus): string | null => {
		if (
			(docStatus.status === 'completed' || docStatus.status === 'approved') &&
			docStatus.path
		) {
			return getFilledPdfUrl(docStatus.path);
		}
		const template = documentTemplates.find((t) => t.id === docStatus.template_id);
		return template?.filePath ? getPdfUrl(template.filePath) : null;
	};

	const pdfUrl = currentStatus ? getDocPdfUrl(currentStatus) : null;

	const isCurrentDocCompleted = currentStatus?.status === 'completed' || currentStatus?.status === 'approved';
	const isCurrentDocEditing = editingDocIndices.has(currentDocIndex);

	React.useEffect(() => {
		// Prevent double initialization - check instance ref, isInitializing flag, and DOM children
		if (viewer.current && !instance.current && !isInitializing.current && viewer.current.children.length === 0) {
			console.log('[OnboardingDoc] Starting WebViewer initialization...');
			isInitializing.current = true;
			
			WebViewer(
				getWebViewerInitOptions('documentSigning', pdfUrl || undefined) as any,
				viewer.current
			).then((inst) => {
				console.log('[OnboardingDoc] WebViewer initialized successfully');
				instance.current = inst;
				setIsViewerReady(true);
				prepareWebViewerInstance(inst, 'documentSigning');

				inst.Core.documentViewer.addEventListener('documentLoaded', () => {
					try {
						setupWebViewerOnDocumentLoaded(inst, 'documentSigning');
					} catch (error) {
						console.error('[OnboardingDoc] Error applying default view settings:', error);
					}
				});
				// Document load is handled by the pdfUrl/isViewerReady effect to avoid double-load
			}).catch((error) => {
				console.error('[OnboardingDoc] WebViewer initialization error:', error);
				showToast('error', 'Failed to initialize PDF viewer');
				isInitializing.current = false;
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (instance.current && pdfUrl && isViewerReady) {
			console.log('Loading document:', pdfUrl);
			// Just load the document, WebViewer will show its own loader
			instance.current.UI.loadDocument(pdfUrl);
			// Zoom will be set automatically by the documentLoaded event listener
		}
	}, [pdfUrl, isViewerReady]);

	React.useEffect(() => {
		if (!isViewerReady || !instance.current) return undefined;
		return attachWebViewerOverscrollToPage(instance.current);
	}, [isViewerReady]);

	// Download handler - hidden for now, kept for future use
	const _handleDownload = async () => {
		if (!instance.current) {
			showToast('error', 'PDF viewer not initialized');
			console.error('Download failed: instance.current is null');
			return;
		}

		try {
			const { documentViewer, annotationManager } = instance.current.Core;
			const doc = documentViewer.getDocument();
			
			if (!doc) {
				showToast('error', 'No document loaded');
				console.error('Download failed: No document loaded');
				return;
			}

			// Get the document data with filled form fields and annotations
			const data = await doc.getFileData({ xfdfString: await annotationManager.exportAnnotations() });
			const arr = new Uint8Array(data);
			const blob = new Blob([arr], { type: 'application/pdf' });
			const link = document.createElement('a');
			link.href = window.URL.createObjectURL(blob);
			link.download = `${currentStatus?.name || 'document'}.pdf`;
			link.click();
			showToast('success', 'Document downloaded successfully');
		} catch (error) {
			showToast('error', 'Failed to download document');
			console.error('Download error:', error);
		}
	};

	const handleMarkAsComplete = async () => {
		if (!instance.current) {
			showToast('error', 'PDF viewer not initialized');
			console.error('Mark complete failed: instance.current is null');
			return;
		}

		if (!currentStatus || !currentTemplate) {
			showToast('error', 'Unable to process document - missing document information');
			console.error('Mark complete failed: currentStatus or currentTemplate is null', {
				currentStatus,
				currentTemplate,
			});
			return;
		}

		setIsUploading(true);
		try {
			const { documentViewer, annotationManager } = instance.current.Core;
			const doc = documentViewer.getDocument();

			if (!doc) {
				showToast('error', 'No document loaded');
				console.error('Mark complete failed: No document loaded');
				setIsUploading(false);
				return;
			}

			// Get the document data with filled form fields and annotations
			const xfdfString = await annotationManager.exportAnnotations();
			const data = await doc.getFileData({ xfdfString });
			const arr = new Uint8Array(data);
			const blob = new Blob([arr], { type: 'application/pdf' });
			const file = new File([blob], `${currentStatus.name}.pdf`, { type: 'application/pdf' });

			await uploadOnboardingPhaseTwoDocument(file, onboardingId, currentStatus.template_id);
			showToast('success', 'Document submitted successfully');

			// Clear editing state for this document
			setEditingDocIndices((prev) => {
				const next = new Set(prev);
				next.delete(currentDocIndex);
				return next;
			});

			// Refresh from server to get the real file path (avoids 404 when revisiting completed docs)
			await onRefresh();

			// Build updated statuses for navigation logic (status only, path comes from server refresh)
			const updatedStatuses = localDocStatuses.map((doc, idx) =>
				idx === currentDocIndex ? { ...doc, status: 'completed' } : doc
			);

			// Find next pending document and navigate to it
			const nextPendingIndex = updatedStatuses.findIndex(
				(doc, idx) =>
					idx > currentDocIndex &&
					doc.status !== 'completed' &&
					doc.status !== 'approved'
			);

			if (nextPendingIndex !== -1) {
				setCurrentDocIndex(nextPendingIndex);
			} else {
				// Check if ALL docs (including before current) are done
				const allCompleted = updatedStatuses.every(
					(doc) => doc.status === 'completed' || doc.status === 'approved'
				);
				if (allCompleted) {
					onAllDocumentsComplete();
				} else {
					// There are still pending docs before current, wrap around to first pending
					const firstPendingIndex = updatedStatuses.findIndex(
						(doc) => doc.status !== 'completed' && doc.status !== 'approved'
					);
					if (firstPendingIndex !== -1) {
						setCurrentDocIndex(firstPendingIndex);
					}
				}
			}
		} catch (error) {
			showToast('error', 'Failed to submit document');
			console.error('Upload error:', error);
		} finally {
			setIsUploading(false);
		}
	};

	const pendingDocuments = documents.filter(
		(doc) => doc.status !== 'completed' && doc.status !== 'approved'
	);

	const getStatusIcon = (status: string) => {
		if (status === 'completed' || status === 'approved') {
			return <CheckCircleIcon color="success" />;
		}
		return <PendingIcon sx={{ color: 'rgb(68, 99, 136)' }} />;
	};

	const getDocStatusLabel = (docStatus: string, idx: number): string => {
		if (editingDocIndices.has(idx)) return 'Editing';
		if (docStatus === 'completed' || docStatus === 'approved') return 'Completed';
		return 'Pending';
	};

	const actionBarSx = {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: 1.5,
		mt: 1,
		px: 1,
		pb: 0.5,
		...(isTabletViewport
			? {
					position: 'sticky',
					bottom: 0,
					zIndex: 3,
					backgroundColor: '#fff',
					pt: 1,
					pb: 'max(8px, env(safe-area-inset-bottom))',
			  }
			: {}),
	} as const;

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
			{/* Tablet: horizontal document selector */}
			{isTabletViewport && (
				<Box
					sx={{
						display: 'flex',
						gap: 1,
						overflowX: 'auto',
						pb: 1,
						mb: 1,
						WebkitOverflowScrolling: 'touch',
					}}
				>
					{documents.map((doc, idx) => {
						const docStatus = doc.status || 'pending';
						const isSelected = idx === currentDocIndex;
						const isDone = docStatus === 'completed' || docStatus === 'approved';
						return (
							<Chip
								key={doc.template_id || idx}
								label={doc.name || doc.path}
								icon={getStatusIcon(docStatus)}
								onClick={() => setCurrentDocIndex(idx)}
								color={isDone ? 'success' : isSelected ? 'primary' : 'default'}
								variant={isSelected ? 'filled' : 'outlined'}
								sx={{
									flexShrink: 0,
									height: 'auto',
									minHeight: 44,
									fontSize: '1rem',
									'& .MuiChip-label': { whiteSpace: 'normal', py: 0.75 },
									'& .MuiChip-icon': { fontSize: 22 },
								}}
							/>
						);
					})}
				</Box>
			)}

			{/* Main Content: Document List Sidebar + PDF Viewer */}
			<Grid container spacing={isTabletViewport ? 1 : 2} sx={{ flex: 1, minHeight: 0 }}>
				{/* Left Sidebar - Document List/Index (desktop only) */}
				{!isTabletViewport && (
				<Grid item xs={12} md={3} lg={2}>
					<Paper sx={{ backgroundColor: '#fff', position: 'sticky', top: 8 }}>
						<Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #e0e0e0' }}>
							<Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
								Documents
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{pendingDocuments.length} pending
							</Typography>
						</Box>
						<List
							dense
							sx={{ maxHeight: 'calc(100vh - 140px)', overflow: 'auto', py: 0 }}
						>
						{documents.map((doc, idx) => {
							const docStatus = doc.status || 'pending';
							return (
								<React.Fragment key={doc.template_id || idx}>
										<ListItem disablePadding>
											<ListItemButton
												selected={idx === currentDocIndex}
												onClick={() => setCurrentDocIndex(idx)}
												sx={{
													'&.Mui-selected': {
														backgroundColor: 'rgba(68, 99, 136, 0.08)',
														'&:hover': {
															backgroundColor: 'rgba(68, 99, 136, 0.12)',
														},
													},
												}}
											>
												<ListItemIcon>{getStatusIcon(docStatus)}</ListItemIcon>
												<ListItemText
													primary={doc.name || doc.path}
													secondary={
														editingDocIndices.has(idx)
															? 'Editing'
															: docStatus === 'completed' || docStatus === 'approved'
																? 'Completed'
																: 'Pending'
													}
												/>
											</ListItemButton>
										</ListItem>
										{idx < documents.length - 1 && <Divider />}
									</React.Fragment>
								);
							})}
						</List>
					</Paper>
				</Grid>
				)}

				{/* Right Side - PDF Viewer */}
				<Grid
					item
					xs={12}
					md={isTabletViewport ? 12 : 9}
					lg={isTabletViewport ? 12 : 10}
				>
					<Paper sx={{ p: isTabletViewport ? 0.5 : 1, backgroundColor: '#fff' }}>
						<Typography
							variant={isTabletViewport ? 'subtitle1' : 'subtitle2'}
							sx={{
								px: 1,
								mb: 0.5,
								fontWeight: 600,
								color: 'rgb(68, 99, 136)',
								fontSize: isTabletViewport ? '1.125rem' : undefined,
							}}
						>
							{currentStatus?.name || 'Document'}
						</Typography>
						{isTabletViewport && currentStatus && (
							<Typography variant="body2" color="text.secondary" sx={{ px: 1, mb: 0.5 }}>
								Status: {getDocStatusLabel(currentStatus.status || 'pending', currentDocIndex)}
							</Typography>
						)}
						<Box ref={viewer} sx={getWebViewerContainerSx(isTabletViewport, 'onboardingInline')} />
					{/* Action Buttons — submit only when the document can be saved */}
					{!readOnly && (!isCurrentDocCompleted || isCurrentDocEditing) && (
					<Box sx={actionBarSx}>
						<Button 
							variant="contained"
							size={isTabletViewport ? 'large' : 'medium'}
							sx={{
								backgroundColor: 'rgb(68, 99, 136)',
								fontSize: isTabletViewport ? '1rem' : undefined,
							}}
							onClick={handleMarkAsComplete}
							disabled={!isViewerReady || isUploading}
						>
							{isUploading ? 'Submitting...' : isCurrentDocEditing ? 'Update & Save' : 'Mark as Complete'}
						</Button>
					</Box>
					)}
					{!readOnly && isCurrentDocCompleted && !isCurrentDocEditing && (
					<Box sx={{ ...actionBarSx, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
						<Button
							variant="outlined"
							size={isTabletViewport ? 'large' : 'medium'}
							startIcon={<EditIcon />}
							sx={{
								color: 'rgb(68, 99, 136)',
								borderColor: 'rgb(68, 99, 136)',
								fontSize: isTabletViewport ? '1rem' : undefined,
								'&:hover': { borderColor: 'rgb(58, 89, 126)', backgroundColor: 'rgba(68, 99, 136, 0.04)' },
							}}
							onClick={() => {
								setEditingDocIndices((prev) => new Set(prev).add(currentDocIndex));
							}}
						>
							Edit Document
						</Button>
						{pendingDocuments.length === 0 &&
							currentDocIndex === documents.length - 1 && (
							<Button
								variant="contained"
								size={isTabletViewport ? 'large' : 'medium'}
								sx={{
									backgroundColor: 'rgb(68, 99, 136)',
									fontSize: isTabletViewport ? '1rem' : undefined,
									'&:hover': { backgroundColor: 'rgb(58, 89, 126)' },
								}}
								onClick={onAllDocumentsComplete}
							>
								Continue
							</Button>
						)}
					</Box>
					)}
					</Paper>
				</Grid>
			</Grid>
		</Box>
	);
}
